import { DataError } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import type { FieldError } from 'react-hook-form'
import { z } from 'zod'

/**
 * Сообщения валидации Zod — коды, а не английский текст: формы переводят их через i18n.
 * Вызывается один раз при старте приложения.
 */
export function setupValidationMessages(): void {
  z.config({
    customError: (issue) => {
      if (issue.code === 'too_small') {
        if (issue.origin === 'number' || issue.origin === 'bigint') return 'too_small'
        return issue.minimum === 1 ? 'required' : 'min_length'
      }
      if (issue.code === 'too_big') return issue.origin === 'number' ? 'too_big' : 'max_length'
      if (issue.code === 'invalid_format') {
        if (issue.format === 'email') return 'email'
        if (issue.format === 'url') return 'url'
        if (issue.format === 'uuid') return 'uuid'
        return 'pattern'
      }
      if (issue.code === 'invalid_type') {
        if (issue.input === undefined) return 'required'
        if (issue.expected === 'int') return 'not_integer'
        if (issue.expected === 'number') return 'not_number'
      }
      if (issue.code === 'invalid_value') return 'invalid'
      return undefined
    },
  })
}

const KNOWN_VALIDATION = new Set([
  'required',
  'email',
  'min_length',
  'max_length',
  'too_small',
  'too_big',
  'not_integer',
  'not_number',
  'pattern',
  'date',
  'partial_date',
  'year_range_order',
  'money_amount',
  'grade_scale',
  'grade_value',
  'duplicates',
  'url',
  'uuid',
  'code_format',
  'invalid',
  'ref_not_found',
  'unknown_system',
  'in_use',
])
const KNOWN_ERRORS = new Set([
  'INVALID_EMAIL_OR_PASSWORD',
  'RATE_LIMITED',
  'INVITE_INVALID',
  'EMAIL_MISMATCH',
  'EMAIL_TAKEN',
  'INVALID_TOKEN',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NETWORK',
  'CODE_TAKEN',
  'NOT_FOUND',
  'VERSION_CONFLICT',
  'VALIDATION_FAILED',
  'UNSUPPORTED_FORMAT',
  'FILE_TOO_LARGE',
  'NUMISTA_NOT_CONFIGURED',
  'NUMISTA_QUOTA',
  'NUMISTA_UNAVAILABLE',
  'INVALID_FILE',
  'NAME_TAKEN',
  'INBOX_PROTECTED',
  'CYCLE',
  'IN_USE',
])

export function useErrorText() {
  const { t } = useTranslation()
  return {
    /** Текст ошибки запроса (DataError) для пользователя. */
    request(error: unknown): string | undefined {
      if (!error) return undefined
      const code = error instanceof DataError ? error.code : 'UNKNOWN'
      // «Используется» приходит как ошибка проверки без поля формы — показываем её смысл.
      if (error instanceof DataError && error.issues.some((i) => i.message === 'in_use'))
        return t('errors.IN_USE')
      return KNOWN_ERRORS.has(code) ? t(`errors.${code}` as 'errors.UNKNOWN') : t('errors.UNKNOWN')
    },
    /** Текст ошибки поля формы (для составных значений — первая вложенная ошибка). */
    field(error: FieldError | FieldErrorTree | undefined): string | undefined {
      const message = firstMessage(error)
      if (!message) return undefined
      return KNOWN_VALIDATION.has(message)
        ? t(`errors.validation.${message}` as 'errors.validation.required')
        : message
    },
  }
}

type FieldErrorTree = { message?: string } | { [key: string]: FieldErrorTree | undefined }

function firstMessage(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined
  const own = (error as { message?: unknown }).message
  if (typeof own === 'string' && own) return own
  for (const [key, value] of Object.entries(error)) {
    if (key === 'ref' || key === 'type') continue
    const nested = firstMessage(value)
    if (nested) return nested
  }
  return undefined
}
